export type BoardRole = "owner" | "member" | "guest";

export type BoardMember = { userId: string; name: string; avatarUrl?: string | null; addedAt: number };

export type BoardMeta = {
  id: string;
  name: string;
  ownerId: string;
  ownerName: string;
  sharedWith: BoardMember[];
  originChannelId: string | null;
  guildId: string | null;
  createdAt: number;
  updatedAt: number;
  role: BoardRole | null;
  live: boolean;
};

export type ShareCandidate = {
  userId: string;
  name: string;
  avatarUrl: string | null;
  source: string;
};

let authToken: string | null = null;
let devIdentity: { id: string; name: string } | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

/** Only used when running outside Discord, so tabs can act as different people. */
export function setDevIdentity(identity: { id: string; name: string } | null): void {
  devIdentity = identity;
}

export function getDevIdentity(): { id: string; name: string } | null {
  return devIdentity;
}

export function getAuthToken(): string | null {
  return authToken;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      // Only declare a JSON body when there is one — Fastify rejects an empty
      // body that claims to be JSON with 400.
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(devIdentity ? { "X-Dev-User": devIdentity.id, "X-Dev-Name": devIdentity.name } : {}),
      ...init.headers,
    },
  });
  if (!response.ok) throw new Error(`${init.method ?? "GET"} ${path} → ${response.status}`);
  return (await response.json()) as T;
}

export const api = {
  listBoards: () => request<{ mine: BoardMeta[]; shared: BoardMeta[] }>("/api/boards"),
  getBoard: (id: string) => request<{ board: BoardMeta }>(`/api/boards/${id}`),
  createBoard: (input: { name?: string; channelId?: string | null; guildId?: string | null }) =>
    request<{ board: BoardMeta }>("/api/boards", { method: "POST", body: JSON.stringify(input) }),
  renameBoard: (id: string, name: string) =>
    request<{ board: BoardMeta }>(`/api/boards/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  deleteBoard: (id: string) => request<{ ok: true }>(`/api/boards/${id}`, { method: "DELETE" }),
  duplicateBoard: (id: string) =>
    request<{ board: BoardMeta }>(`/api/boards/${id}/duplicate`, { method: "POST" }),
  exportBoard: (id: string) => request<unknown>(`/api/boards/${id}/export`),
  shareCandidates: (id: string, query = "") =>
    request<{ candidates: ShareCandidate[] }>(
      `/api/boards/${id}/share-candidates${query ? `?q=${encodeURIComponent(query)}` : ""}`,
    ),
  addShare: (id: string, member: { userId: string; name: string; avatarUrl?: string | null }) =>
    request<{ board: BoardMeta }>(`/api/boards/${id}/share`, {
      method: "POST",
      body: JSON.stringify(member),
    }),
  removeShare: (id: string, userId: string) =>
    request<{ board: BoardMeta }>(`/api/boards/${id}/share/${userId}`, { method: "DELETE" }),
};
